import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, errorMessage } from '../lib/api';
import type { ResultRow } from '../lib/results';
import { SLOT_NAMES, clockTime, dayDate, dayKey, dayLabel, type SessionView } from '../lib/sessions';
import type { PublicUser } from '../lib/types';
import { comingUp, firstName, greeting, justIn, lastSevenDays, sinceLabel, waitingForMarks } from '../lib/today';
import { useAuth } from '../store/auth';
import { Avatar, Person } from '../components/Avatar';
import { Band } from '../components/Band';
import { ModuleTag } from '../components/ModuleTag';
import { Icon, type IconName } from '../components/Icon';
import { Banner } from '../components/Banner';

/**
 * Where a teacher's day starts.
 *
 * Every other screen is a list of one kind of thing. This one is the morning
 * question — what needs me, what is coming, what has happened since I last
 * looked — answered from the same lists the other screens show, so nothing
 * here can disagree with them. It greets the person by name because it is
 * theirs: a teacher sees their own candidates and their own test days.
 */

const START: { to: string; icon: IconName; title: string; body: string }[] = [
  { to: '/students', icon: 'users', title: 'Enrol students', body: 'One at a time, or a whole class' },
  { to: '/tests', icon: 'book', title: 'Set a paper', body: 'From the library, on a class' },
  { to: '/schedule', icon: 'calendar', title: 'Plan a test day', body: 'Morning, afternoon or evening' },
];

export function TodayPage() {
  const user = useAuth((s) => s.user);
  const [results, setResults] = useState<ResultRow[] | null>(null);
  const [sessions, setSessions] = useState<SessionView[] | null>(null);
  const [students, setStudents] = useState<PublicUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => new Date());

  // The greeting follows the clock through a long day at the desk.
  useEffect(() => {
    const tick = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(tick);
  }, []);

  useEffect(() => {
    const today = new Date();
    const fortnight = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 13);
    Promise.all([
      api.get<{ results: ResultRow[] }>('/results', {
        params: { sort: 'graded', direction: 'desc', limit: 200 },
      }),
      api.get<{ sessions: SessionView[] }>('/sessions', {
        params: { from: dayKey(today), to: dayKey(fortnight) },
      }),
      api.get<{ members: PublicUser[] }>('/users', { params: { role: 'STUDENT' } }),
    ])
      .then(([r, s, u]) => {
        setResults(r.data.results);
        setSessions(s.data.sessions);
        setStudents(u.data.members);
      })
      .catch((err) => setError(errorMessage(err, 'Could not load your day')));
  }, []);

  const waiting = useMemo(() => (results ? waitingForMarks(results) : []), [results]);
  const recent = useMemo(() => (results ? justIn(results) : []), [results]);
  const week = useMemo(() => lastSevenDays(results ?? [], now), [results, now]);
  const upcoming = useMemo(() => (sessions ? comingUp(sessions, now) : []), [sessions, now]);
  const today = upcoming.filter((s) => s.day === dayKey(now));

  if (!user) return null;
  const loading = results === null || sessions === null || students === null;

  const pulse = [
    now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' }),
    loading
      ? null
      : waiting.length
        ? `${waiting.length} ${waiting.length === 1 ? 'script' : 'scripts'} waiting for you`
        : 'nothing waiting to be marked',
    loading
      ? null
      : today.length
        ? `${today.length === 1 ? 'a test day' : `${today.length} test days`} on today`
        : 'no test day today',
  ].filter(Boolean);

  return (
    <div className="daypage">
      <header className="today__head rise">
        <div className="today__hello">
          <Avatar name={user.fullName} size="lg" />
          <div>
            <h1 className="today__title">{greeting(firstName(user.fullName), now)}</h1>
            <p className="today__pulse">{pulse.join(' · ')}</p>
          </div>
        </div>
      </header>

      {error && <Banner tone="error">{error}</Banner>}

      <nav className="today__start" aria-label="Get started">
        {START.map((item) => (
          <Link key={item.to} to={item.to} className="starttile">
            <span className="starttile__icon" aria-hidden="true">
              <Icon name={item.icon} size={20} />
            </span>
            <span>
              <span className="starttile__title">{item.title}</span>
              <span className="starttile__body">{item.body}</span>
            </span>
            <Icon name="arrowRight" size={16} className="starttile__go" />
          </Link>
        ))}
      </nav>

      <div className="today__grid">
        <div className="today__col">
          <section className="daycard daycard--waiting">
            <header className="daycard__head">
              <h2>Waiting for you</h2>
              {waiting.length > 0 && <span className="daycard__count">{waiting.length}</span>}
            </header>
            {loading ? (
              <Skeleton rows={3} />
            ) : waiting.length === 0 ? (
              <div className="caughtup">
                <span className="caughtup__icon" aria-hidden="true">
                  <Icon name="coffee" size={22} />
                </span>
                <div>
                  <p className="caughtup__title">All caught up</p>
                  <p className="caughtup__body">
                    Every Writing script handed in has been marked. Enjoy the quiet.
                  </p>
                </div>
              </div>
            ) : (
              <ul className="daylist">
                {waiting.slice(0, 5).map((row) => (
                  <li key={row.id}>
                    <Person name={row.student.fullName} detail={row.test.title} />
                    <span className="daylist__when">handed in {sinceLabel(row.gradedAt, now)}</span>
                    <Link className="btn btn--sm btn--primary" to={`/results/${row.id}`}>
                      Mark
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {waiting.length > 5 && (
              <Link className="daycard__more" to="/results">
                {waiting.length - 5} more in Results <Icon name="arrowRight" size={14} />
              </Link>
            )}
          </section>

          <section className="daycard">
            <header className="daycard__head">
              <h2>Just in</h2>
              <Link className="daycard__link" to="/results">
                All results
              </Link>
            </header>
            {loading ? (
              <Skeleton rows={4} />
            ) : recent.length === 0 ? (
              <p className="daycard__empty">
                No marked papers yet. They land here the moment a candidate hands one in.
              </p>
            ) : (
              <ul className="daylist">
                {recent.map((row) => (
                  <li key={row.id}>
                    <Person name={row.student.fullName} detail={row.test.title} />
                    <ModuleTag module={row.test.module} />
                    <span className="daylist__when">{sinceLabel(row.gradedAt, now)}</span>
                    <Band value={row.bandScore} />
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <div className="today__col">
          <section className="daycard daycard--week">
            <header className="daycard__head">
              <h2>The last seven days</h2>
            </header>
            <dl className="weekfigs">
              <div>
                <dt>Papers handed in</dt>
                <dd>{loading ? '–' : week.handedIn}</dd>
              </div>
              <div>
                <dt>Average band</dt>
                <dd>{loading ? '–' : <Band value={week.averageBand} size="lg" />}</dd>
              </div>
              <div>
                <dt>Candidates who sat</dt>
                <dd>
                  {loading ? '–' : week.candidates}
                  {!loading && students && <span className="weekfigs__of"> of {students.length}</span>}
                </dd>
              </div>
            </dl>
          </section>

          <section className="daycard">
            <header className="daycard__head">
              <h2>Coming up</h2>
              <Link className="daycard__link" to="/schedule">
                Timetable
              </Link>
            </header>
            {loading ? (
              <Skeleton rows={2} />
            ) : upcoming.length === 0 ? (
              <div className="daycard__empty">
                <p>Nothing planned for the next two weeks.</p>
                <Link className="btn btn--sm" to="/schedule">
                  Plan a test day
                </Link>
              </div>
            ) : (
              <ul className="sessionlist">
                {upcoming.map((session) => (
                  <li key={session.id} className={session.state === 'RUNNING' ? 'is-live' : undefined}>
                    <Link to={`/schedule/${session.id}`}>
                      <span className="sessionlist__day">
                        <span className="sessionlist__weekday">{shortDay(dayLabel(session.day, now))}</span>
                        <span className="sessionlist__date">{dayDate(session.day)}</span>
                      </span>
                      <span className="sessionlist__what">
                        <span className="sessionlist__slot">
                          {session.state === 'RUNNING' && <i className="livedot" aria-hidden="true" />}
                          {session.name ?? SLOT_NAMES[session.slot]} · {clockTime(session.startsAt)}
                        </span>
                        <span className="sessionlist__detail">
                          {session.state === 'RUNNING'
                            ? 'Happening now'
                            : `${session.candidates.length} ${session.candidates.length === 1 ? 'candidate' : 'candidates'} · ${session.papers.length} ${session.papers.length === 1 ? 'paper' : 'papers'}`}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

/** "Wednesday" is too long for the little calendar leaf; "Today" and "Tomorrow" are not. */
const shortDay = (label: string): string =>
  label === 'Today' || label === 'Tomorrow' ? label : label.slice(0, 3);

function Skeleton({ rows }: { rows: number }) {
  return (
    <div className="skeleton" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <span key={i} />
      ))}
    </div>
  );
}
